/**
 * Screens 6 + 7 — Matchmaking countdown + live gameplay.
 *
 * Opens /ws/game and renders one of four sub-states from the engine:
 *   - countdown: opponent vs you + huge ticking number (screen 6)
 *   - prompt: scoreboard row, timer bar, question card, answer tiles (screen 7)
 *   - reveal: same as prompt but with correct/wrong styling on tiles
 *   - finished: nudge to /result
 *
 * The remaining-ms bar uses requestAnimationFrame against `ends_at_ms` from
 * the server — local clock drift only affects the bar, never the score.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { gameAudio, mountAudioToggle } from '../lib/game_audio.js';
import { preloadImage, questionImageHtml } from '../lib/question_image.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { TrivianaSocket } from '../ws.js';

export async function renderMatch(root) {
  const matchId = window.location.pathname.split('/').pop();
  root.innerHTML = `
    <div class="appbar" id="match-appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title" id="match-title">Match</div>
      <div class="appbar-spacer"></div>
      <span class="status-pill warn" id="match-state-pill">connecting</span>
    </div>
    <div id="match-page" style="background:var(--paper);">
      <div class="pad"><div class="skel"></div><div class="skel"></div></div>
    </div>
  `;
  // Gameplay ambient — muted by default. The toggle in the appbar is the
  // user gesture that unlocks the AudioContext on mobile browsers.
  const detachAudio = mountAudioToggle(document.getElementById('match-appbar'));
  gameAudio.start();

  // REST hydrate so we can render the lobby even before WS is up.
  let initial;
  try {
    initial = await api(`/match/${matchId}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) { navigate('/login'); return; }
    document.getElementById('match-page').innerHTML =
      `<div class="pad"><div class="card" style="color:var(--pink-t)">${escape(err.message)}</div></div>`;
    return;
  }

  document.getElementById('match-title').textContent =
    initial.mode === 'solo' ? 'Solo practice' : '1-on-1 duel';
  if (initial.status === 'finished') {
    navigate(`/match/${matchId}/result`);
    return;
  }

  const me = auth.getMe();
  const myUserId = me?.id || null;

  const state = {
    matchId,
    mode: initial.mode,
    questionCount: initial.question_count,
    answerWindowMs: initial.answer_window_ms,
    mySeat: null,
    opponentUserId: null,
    score: 0,
    opponentScore: 0,
    currentOrder: -1,
    currentEndsAt: 0,
    answered: false,
    finished: false,
    socket: null,
    timerRaf: null,
  };

  const players = initial.players || [];
  if (myUserId && players.length === 2) {
    const opp = players.find((p) => p.user_id !== myUserId);
    state.opponentUserId = opp?.user_id || null;
  }

  paintLobby(state, initial);

  const socket = new TrivianaSocket(
    `/ws/game?match_id=${encodeURIComponent(matchId)}`,
    { autoReconnect: true },
  );
  state.socket = socket;
  socket.on('open', () => setPill('connected', 'ok'));
  socket.on('close', () => setPill('reconnecting…', 'warn'));
  socket.on('error', () => setPill('connection error', 'bad'));
  socket.on('message', (msg) => onWsMessage(state, msg));
  socket.connect();

  const cleanup = () => {
    if (state.timerRaf) cancelAnimationFrame(state.timerRaf);
    socket.close();
    gameAudio.stop();
    detachAudio();
  };
  window.addEventListener('popstate', cleanup, { once: true });
}

function setPill(text, kind) {
  const el = document.getElementById('match-state-pill');
  if (!el) return;
  el.textContent = text;
  el.className = 'status-pill ' + (kind || 'warn');
}

// ---- pre-game lobby --------------------------------------------------------

function paintLobby(state, initial) {
  const page = document.getElementById('match-page');
  page.innerHTML = `
    <div class="pad center">
      <div class="card center indigo" style="background:var(--surf-2);border-color:var(--violet);">
        <div style="font-family:var(--font-display);font-size:13px;color:#B7A6FF;letter-spacing:0.12em;">GET READY</div>
        <div style="font-size:64px;margin:12px 0;">⚡</div>
        <div class="muted">${initial.question_count} questions · ${initial.answer_window_ms / 1000}s each</div>
        <div class="muted" id="match-lobby-status" style="margin-top:8px;font-size:12px;">
          Joining the room…
        </div>
      </div>
    </div>
  `;
}

// Server-driven lobby card. Repaints every time match.lobby arrives so the
// "waiting for opponent" message disappears the moment the opponent connects.
function paintLobbyState(state, msg) {
  const page = document.getElementById('match-page');
  if (!page) return;
  const others = (msg.players || []).filter((p) => p.user_id !== state.myUserId);
  const missing = others.filter((p) => !p.connected);
  let line;
  if (msg.ready) {
    line = 'Both players in — countdown starting…';
  } else if (msg.mode === 'duel' && missing.length > 0) {
    line = 'Waiting for opponent to join…';
  } else {
    line = 'Joining the room…';
  }
  page.innerHTML = `
    <div class="pad center">
      <div class="card center indigo" style="background:var(--surf-2);border-color:var(--violet);">
        <div style="font-family:var(--font-display);font-size:13px;color:#B7A6FF;letter-spacing:0.12em;">GET READY</div>
        <div style="font-size:64px;margin:12px 0;">⚡</div>
        <div class="muted">${state.questionCount} questions · ${state.answerWindowMs / 1000}s each</div>
        <div class="muted" id="match-lobby-status" style="margin-top:8px;font-size:12px;">
          ${line}
        </div>
      </div>
    </div>
  `;
  setPill(msg.ready ? 'starting' : 'waiting', msg.ready ? 'ok' : 'warn');
}

// ---- WS dispatch -----------------------------------------------------------

function onWsMessage(state, msg) {
  if (!msg || typeof msg !== 'object') return;
  switch (msg.type) {
    case 'match.hello': {
      state.mySeat = msg.your_seat;
      state.myUserId = msg.your_user_id;
      state.questionCount = msg.question_count;
      state.answerWindowMs = msg.answer_window_ms;
      const opp = (msg.players || []).find((p) => p.user_id !== msg.your_user_id);
      if (opp) state.opponentUserId = opp.user_id;
      if (msg.your_user_id) {
        const cached = auth.getMe() || {};
        if (!cached.id) auth.setMe({ ...cached, id: msg.your_user_id });
      }
      break;
    }
    case 'match.lobby': paintLobbyState(state, msg); break;
    case 'match.countdown': paintCountdown(state, msg); break;
    case 'match.started':
      setPill('LIVE', 'bad');
      // No new paint here — the next question.prompt will paint the gameplay.
      break;
    case 'question.prompt': paintPrompt(state, msg); break;
    case 'question.preload':
      // Server hint that the next question has an image — prefetch it during
      // the reveal window so it's already in cache when the question fires.
      preloadImage(msg.image_url);
      break;
    case 'answer.ack':
      state.score = msg.score;
      updateScoreboard(state);
      lockChoices(true);
      break;
    case 'question.reveal': paintReveal(state, msg); break;
    case 'match.finished': paintFinished(state, msg); break;
    case 'pong': break;
  }
}

// ---- countdown (screen 6) --------------------------------------------------

function paintCountdown(state, msg) {
  const page = document.getElementById('match-page');
  page.innerHTML = `
    <div class="hero dark center flush" style="min-height:calc(100dvh - 64px);padding-top:70px;">
      <span class="tag vio">Finding opponent</span>
      <div class="row" style="justify-content:center;gap:26px;margin:54px 0 16px;">
        <div class="center">
          <div class="av lg">${seatInitial(state.mySeat ?? 0)}</div>
          <div style="font-family:var(--font-display);margin-top:9px;font-weight:600;color:#fff;">You</div>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">${state.mode === 'solo' ? 'Practice' : 'Player'}</div>
        </div>
        ${state.mode === 'solo' ? '' : `
          <div style="font-family:var(--font-display);font-size:24px;color:var(--mute);font-weight:600;align-self:center;">vs</div>
          <div class="center">
            <div class="av lg">?</div>
            <div style="font-family:var(--font-display);margin-top:9px;font-weight:600;color:#fff;">Opponent</div>
            <div style="font-size:11px;color:var(--mute);font-weight:500;">Player</div>
          </div>
        `}
      </div>
      <div id="countdown-num" style="font-family:var(--font-display);font-size:78px;color:#fff;font-weight:600;line-height:1.1;margin-top:30px;">3</div>
      <div class="muted">Get ready…</div>
    </div>
  `;
  const startsAtMs = msg.starts_at_ms || Date.now() + (msg.duration_ms || 3000);
  function tick() {
    const remaining = Math.max(0, startsAtMs - Date.now());
    const sec = Math.ceil(remaining / 1000);
    const el = document.getElementById('countdown-num');
    if (el) el.textContent = String(sec);
    if (remaining > 0) requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

function seatInitial(seat) {
  return seat === 1 ? 'B' : 'A';
}

// ---- gameplay prompt (screen 7) -------------------------------------------

// First TAP_WARMUP_MS after a question renders, taps are ignored. Catches the
// mobile scroll-vs-tap ambiguity where a flick over a freshly-painted button
// can register as a click — the user gets a "system picked for me" surprise
// when the reveal fires on what was actually an accidental tap.
const TAP_WARMUP_MS = 350;

function paintPrompt(state, msg) {
  state.currentOrder = msg.order;
  state.currentEndsAt = msg.ends_at_ms || Date.now() + msg.window_ms;
  state.answered = false;
  state.promptShownAt = Date.now();
  const total = msg.total || state.questionCount;
  const page = document.getElementById('match-page');
  page.innerHTML = `
    <div class="pad" style="padding-bottom:6px;">
      <div class="row">
        <a href="/" data-link class="appbar-back" aria-label="Leave">‹</a>
        <div class="grow">
          <div class="xpbar teal" style="height:9px;">
            <i style="width:${Math.round(((msg.order) / total) * 100)}%;"></i>
          </div>
        </div>
      </div>
      <div class="row" style="margin-top:9px;">
        <span style="font-size:12px;color:var(--mute);font-weight:600;">Question ${msg.order + 1} / ${total}</span>
        <div class="sp"></div>
        <span style="font-size:12px;font-weight:600;color:var(--ink-2);" id="scoreboard">
          You <b style="color:#fff;" id="my-score">${state.score}</b>${state.mode === 'solo' ? '' : ` · Opp <b style="color:var(--ink-2);" id="opp-score">${state.opponentScore}</b>`}
        </span>
      </div>
    </div>

    <div class="pad" style="padding-top:4px;">
      <div class="timer"><i id="qbar" style="width:100%;"></i></div>
      <div class="center" style="font-family:var(--font-display);font-size:13px;color:var(--gold-t);margin:8px 0 15px;font-weight:600;" id="qtimer">
        ${(state.answerWindowMs / 1000).toFixed(0)}s left
      </div>

      ${questionImageHtml(msg.image_url)}

      <div class="card" style="min-height:88px;display:flex;align-items:center;">
        <b style="font-size:17px;font-family:var(--font-display);font-weight:600;line-height:1.35;color:#fff;white-space:pre-wrap;">${escape(msg.prompt)}</b>
      </div>

      <div id="choices"${msg.image_url ? ' class="opts-grid"' : ''}>
        ${(msg.choices || []).map((c, i) => `
          <button type="button" class="opt choice-btn" data-index="${i}">
            <span>${String.fromCharCode(65 + i)}. ${escape(c)}</span>
            <span aria-hidden="true"></span>
          </button>
        `).join('')}
      </div>
    </div>
  `;
  document.querySelectorAll('.choice-btn').forEach((btn) =>
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.dataset.index, 10);
      if (isNaN(idx) || state.answered) return;
      // Ignore taps that fire in the first few hundred ms after the question
      // appears — almost always a phantom tap from a scroll gesture, not a
      // deliberate choice.
      if (Date.now() - (state.promptShownAt || 0) < TAP_WARMUP_MS) return;
      state.answered = true;
      lockChoices(true, idx);
      state.socket.send({ type: 'answer', order: state.currentOrder, choice_index: idx });
    }),
  );
  startTimer(state);
}

function lockChoices(disabled, pickedIdx = null) {
  document.querySelectorAll('.choice-btn').forEach((btn, i) => {
    btn.disabled = !!disabled;
    if (pickedIdx === i) {
      btn.classList.add('violet-on');
    }
  });
}

function startTimer(state) {
  if (state.timerRaf) cancelAnimationFrame(state.timerRaf);
  const totalMs = Math.max(1, state.currentEndsAt - Date.now());
  function tick() {
    const remaining = Math.max(0, state.currentEndsAt - Date.now());
    const pct = Math.max(0, Math.min(100, (remaining / totalMs) * 100));
    const tEl = document.getElementById('qtimer');
    const bar = document.getElementById('qbar');
    if (tEl) tEl.textContent = `${(remaining / 1000).toFixed(1)}s left`;
    if (bar) bar.style.width = `${pct}%`;
    if (remaining > 0 && !state.finished) {
      state.timerRaf = requestAnimationFrame(tick);
    }
  }
  state.timerRaf = requestAnimationFrame(tick);
}

function updateScoreboard(state) {
  const my = document.getElementById('my-score');
  const opp = document.getElementById('opp-score');
  if (my) my.textContent = String(state.score);
  if (opp && state.mode !== 'solo') opp.textContent = String(state.opponentScore);
}

// ---- reveal ---------------------------------------------------------------

function paintReveal(state, msg) {
  if (state.opponentUserId) {
    const oppTotal = (msg.totals || []).find((t) => t.user_id === state.opponentUserId);
    if (oppTotal) state.opponentScore = oppTotal.score;
  }
  const myTotal = (msg.totals || []).find((t) => t.seat === state.mySeat);
  if (myTotal) state.score = myTotal.score;

  const myThis = myTotal?.this_question || null;
  document.querySelectorAll('.choice-btn').forEach((btn, i) => {
    btn.disabled = true;
    btn.classList.remove('violet-on');
    if (i === msg.correct_index) {
      btn.classList.add('correct');
      // Append a check tick if not already present.
      if (!btn.querySelector('.tick')) {
        const tick = document.createElement('span');
        tick.className = 'tick';
        tick.textContent = '✓';
        btn.appendChild(tick);
      }
    } else if (myThis && myThis.choice_index === i) {
      btn.classList.add('wrong');
    }
  });
  updateScoreboard(state);

  const tEl = document.getElementById('qtimer');
  if (tEl) {
    if (!myThis) tEl.textContent = '—';
    else if (myThis.correct) tEl.textContent = `+${myThis.points ?? 0} pts`;
    else tEl.textContent = '0 pts';
  }
}

// ---- finish ---------------------------------------------------------------

function paintFinished(state, _msg) {
  state.finished = true;
  if (state.timerRaf) cancelAnimationFrame(state.timerRaf);
  setPill('finished', 'ok');
  toast.success('Match finished — loading results…');
  setTimeout(() => navigate(`/match/${state.matchId}/result`), 600);
}
